/* eslint no-bitwise: 0 */
type StringLike = { toString(): string };

export default class RandomDataGenerator {
  private c = 1;

  private s0 = 0;

  private s1 = 0;

  private s2 = 0;

  constructor(seeds: StringLike[] | string) {
    if (typeof seeds === 'string') {
      this.state(seeds);
    } else {
      this.sow(seeds);
    }
  }

  public rnd(): number {
    const t: number = 2091639 * this.s0 + this.c * 2.3283064365386963e-10;
    this.c = t | 0;
    this.s0 = this.s1;
    this.s1 = this.s2;
    this.s2 = t - this.c;
    return this.s2;
  }

  public sow(seeds?: StringLike[]): void {
    this.s0 = this.hash(' ');
    this.s1 = this.hash(this.s0);
    this.s2 = this.hash(this.s1);
    this.c = 1;

    if (!seeds) {
      return;
    }

    // Apply any seeds
    for (let i = 0; i < seeds.length && seeds[i] != null; i += 1) {
      const seed = seeds[i];

      this.s0 -= this.hash(seed);
      this.s0 += ~~(this.s0 < 0);
      this.s1 -= this.hash(seed);
      this.s1 += ~~(this.s1 < 0);
      this.s2 -= this.hash(seed);
      this.s2 += ~~(this.s2 < 0);
    }
  }

  public shuffle<T>(array: T[]): T[] {
    for (let i: number = array.length - 1; i > 0; i -= 1) {
      const j: number = Math.floor(this.rnd() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
  }

  public integer(): number {
    return this.rnd.apply(this) * 0x100000000;
  }

  public frac(): number {
    return this.rnd.apply(this) + ((this.rnd.apply(this) * 0x200000) | 0) * 1.1102230246251565e-16;
  }

  public real(): number {
    return this.integer() + this.frac();
  }

  public integerInRange(min: number, max: number): number {
    return Math.floor(this.realInRange(0, max - min + 1) + min);
  }

  public between(min: number, max: number): number {
    return this.integerInRange(min, max);
  }

  public realInRange(min: number, max: number): number {
    return this.frac() * (max - min) + min;
  }

  public normal(): number {
    return 1 - 2 * this.frac();
  }

  public uuid(): string {
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    let a = '';
    let b = '';

    for (
      // eslint-disable-next-line no-multi-assign
      b = a = '';
      // @ts-expect-error
      // eslint-disable-next-line no-plusplus
      a++ < 36;
      b +=
        // @ts-expect-error
        ~a % 5 | ((a * 3) & 4)
          ? // @ts-expect-error
            (a ^ 15 ? 8 ^ (this.frac() * (a ^ 20 ? 16 : 4)) : 4).toString(16)
          : '-'
    ) {
      //
    }

    return b;
  }

  public pick<T>(array: T[]): T {
    return array[this.integerInRange(0, array.length - 1)];
  }

  public sign(): number {
    return this.pick([-1, 1]);
  }

  public bool(): boolean {
    return this.sign() === 1;
  }

  public weightedPick<T>(array: T[]): T {
    return array[~~(this.frac() ** 2 * (array.length - 1) + 0.5)];
  }

  public timestamp(min: number, max: number): number {
    return this.realInRange(min || 946684800000, max || 1577862000000);
  }

  public angle(): number {
    return this.integerInRange(-180, 180);
  }

  public state(state?: string): string {
    if (typeof state === 'string' && state.match(/^!rnd/)) {
      const stateArray: string[] = state.split(',');

      this.c = parseFloat(stateArray[1]);
      this.s0 = parseFloat(stateArray[2]);
      this.s1 = parseFloat(stateArray[3]);
      this.s2 = parseFloat(stateArray[4]);
    }

    return ['!rnd', this.c, this.s0, this.s1, this.s2].join(',');
  }

  public pickFromTable<T>(table: [T, number][]): T {
    const total = table.reduce((acc, val) => acc + val[1], 0);
    const random = this.realInRange(0, total);

    let currentAcc = 0;
    return (table.find((val) => {
      currentAcc += val[1];
      return random < currentAcc;
    }) ?? table[table.length - 1])[0];
  }

  private hash(data: StringLike): number {
    let h: number;
    let i: number;
    let n = 0xefc8249d;

    const stringData = data.toString();

    for (i = 0; i < stringData.length; i += 1) {
      n += stringData.charCodeAt(i);
      h = 0.02519603282416938 * n;
      n = h >>> 0;
      h -= n;
      h *= n;
      n = h >>> 0;
      h -= n;
      n += h * 0x100000000; // 2^32
    }

    return (n >>> 0) * 2.3283064365386963e-10; // 2^-32
  }
}
